// Pure replay/scrubber logic (no DOM, no Svelte) so it can be unit-tested.
//
// Playback is a virtual clock running at a multiple of real time ("night speed"): at 60×, one second of playback
// is one minute of election night, so a 4-hour night (7–11 PM) replays in 4 minutes. The view always shows the
// latest snapshot whose time is at or before the clock.

export const SPEEDS = [30, 60, 120, 300] as const;
export type Speed = (typeof SPEEDS)[number];
export const DEFAULT_SPEED: Speed = 60;

/** Tiny LRU of keys. touch() returns the keys evicted to stay within `max`; keys for which `keep` is true are
 *  never evicted (the oldest evictable keys go instead), so a pinned key can't push another one out of the list
 *  without it being reported. */
export class Lru<K> {
  private order: K[] = [];
  constructor(readonly max: number) {}
  touch(k: K, keep: (k: K) => boolean = () => false): K[] {
    const i = this.order.indexOf(k);
    if (i !== -1) this.order.splice(i, 1);
    this.order.push(k);
    const out: K[] = [];
    for (let j = 0; this.order.length > this.max && j < this.order.length - 1; ) {
      const c = this.order[j]!;
      if (keep(c)) { j++; continue; }
      this.order.splice(j, 1);
      out.push(c);
    }
    return out;
  }
  has(k: K) {
    return this.order.includes(k);
  }
  keys(): K[] {
    return [...this.order];
  }
}

export interface TPoint {
  snapshot_id: string;
  t: string;
}

export function pointTimes(points: TPoint[]): number[] {
  return points.map((p) => Date.parse(p.t));
}

/** Index of the last point with time <= t (-1 if t is before the first point). `ts` ascending. */
export function indexAtOrBefore(ts: number[], t: number): number {
  let lo = 0, hi = ts.length - 1, ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (ts[mid]! <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
  }
  return ans;
}

/** Index of the point closest in time to t (ties -> earlier). */
export function nearestIndex(ts: number[], t: number): number {
  if (!ts.length) return -1;
  const i = indexAtOrBefore(ts, t);
  if (i < 0) return 0;
  if (i >= ts.length - 1) return ts.length - 1;
  return t - ts[i]! <= ts[i + 1]! - t ? i : i + 1;
}

/**
 * Advance the playback clock by `dtMs` of wall time at `speed`. Returns the new clock, the snapshot index to
 * show and whether playback reached the end (the last point).
 */
export function playbackStep(ts: number[], clock: number, dtMs: number, speed: number): { clock: number; index: number; done: boolean } {
  const last = ts[ts.length - 1] ?? clock;
  const next = Math.min(last, clock + dtMs * speed);
  return { clock: next, index: Math.max(0, indexAtOrBefore(ts, next)), done: next >= last };
}

/** Snapshot index one step before/after the clock: previous = the snapshot before the one being shown (or the shown
 *  one's start if the clock is past it), next = the first snapshot after the clock. Clamped to the ends. */
export function stepIndex(ts: number[], clock: number, dir: -1 | 1): number {
  if (!ts.length) return -1;
  const i = indexAtOrBefore(ts, clock);
  if (dir > 0) return Math.min(ts.length - 1, i + 1);
  if (i < 0) return 0;
  return clock > ts[i]! + 500 ? i : Math.max(0, i - 1);
}

/** How long a whole night takes to replay at `speed`, in ms of wall time. */
export const playDuration = (ts: number[], speed: number) => (ts.length > 1 ? (ts[ts.length - 1]! - ts[0]!) / speed : 0);

/** Snapshot ids to prefetch after `index` (the next `n`). */
export function prefetchIds(points: TPoint[], index: number, n = 2): string[] {
  return points.slice(index + 1, index + 1 + n).map((p) => p.snapshot_id);
}

const TICK_STEPS_MIN = [1, 2, 5, 10, 15, 30, 60, 120, 180];
const etHM = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' });

/**
 * Time-axis ticks for [t0, t1] on ET wall-clock boundaries (ET offsets are whole hours, so multiples of a step
 * that divides 60 min are also ET boundaries). Labels: "9 PM", "9:30".
 */
export function timeTicks(t0: number, t1: number, maxTicks = 7): { t: number; label: string }[] {
  if (!(t1 > t0)) return [];
  const span = (t1 - t0) / 60_000;
  const step = TICK_STEPS_MIN.find((s) => span / s <= maxTicks) ?? 240;
  const stepMs = step * 60_000;
  const out: { t: number; label: string }[] = [];
  for (let t = Math.ceil(t0 / stepMs) * stepMs; t <= t1; t += stepMs) {
    const parts = etHM.formatToParts(t);
    const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
    const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
    const period = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';
    out.push({ t, label: minute === '00' ? `${hour} ${period}` : `${hour}:${minute}` });
  }
  return out;
}

/** Read/write `at` (replay snapshot) in a hash like "#/race/x?area=y&at=z". */
export function withHashParam(hash: string, key: string, value: string | null): string {
  const raw = hash.replace(/^#/, '') || '/';
  const [path = '/', qs = ''] = raw.split('?');
  const q = new URLSearchParams(qs);
  if (value === null || value === '') q.delete(key);
  else q.set(key, value);
  const s = q.toString();
  return `#${path}${s ? `?${s}` : ''}`;
}

/** Minimal linear scale (keeps d3-scale and its interpolate/format/time deps out of the bundle). */
export function linear(domain: [number, number], range: [number, number]) {
  const [d0, d1] = domain, [r0, r1] = range;
  const k = d1 === d0 ? 0 : (r1 - r0) / (d1 - d0);
  const f = (v: number) => r0 + (v - d0) * k;
  f.invert = (y: number) => (k === 0 ? d0 : d0 + (y - r0) / k);
  return f;
}

// ------------------------------------------------------------------ datasets (live vs static replays)

/** Replay datasets the UI knows about (served from /data/replay/<id>/). */
export const REPLAY_DATASETS = ['2024'] as const;
const ID_RE = /^[A-Za-z0-9_-]{1,32}$/;

/** `?replay=<id>` from a hash query; null = live. Only well-formed ids are accepted. */
export function replayFromQuery(q: URLSearchParams): string | null {
  const v = q.get('replay');
  return v && ID_RE.test(v) ? v : null;
}

/** Base URL for a dataset's files. */
export function datasetBase(replay: string | null): string {
  return replay ? `/data/replay/${replay}/` : '/data/';
}

/** Hash for switching dataset: keeps the route (and area), drops the replay position. */
export function switchDatasetHash(hash: string, replay: string | null): string {
  return withHashParam(withHashParam(hash || '#/', 'at', null), 'replay', replay);
}
